'use client';

import { useCallback, useId, useRef, useState } from 'react';
import { useConsent, useServiceConsentGranted } from '@/features/consent';
import { ChatConsentPrompt } from './ChatConsentPrompt';
import { ChatLauncher } from './ChatLauncher';
import { ChatLoadError } from './ChatLoadError';
import { CHATWERK_SERVICE_ID } from './chat-widget.constants';
import { buildChatwerkConfig } from './chatwerk-config';
import { isChatwerkStarted, startChatwerk } from './chatwerk-loader';
import { useChatWindowFocus } from './useChatWindowFocus';
import { useChatWindowOpen } from './useChatWindowOpen';
import { useCloseOnEscape } from './useCloseOnEscape';

type ChatWidgetProps = {
    organizationId: string;
    privacyHref?: string;
};

type PanelView = 'none' | 'consent' | 'error';

/**
 * Site-wide ChatWerk launcher. Nothing is requested from ChatWerk before a
 * visitor starts the chat: its loader alone weighs ~300 KiB gzip and reports
 * every page it is initialized on. Without a prior grant the first click explains
 * the transfer; starting grants the service — the same switch as in the cookie
 * settings, like the map's consent overlay.
 */
export function ChatWidget({ organizationId, privacyHref }: ChatWidgetProps) {
    const { grantService, isBannerVisible } = useConsent();
    const isGranted = useServiceConsentGranted(CHATWERK_SERVICE_ID);
    const isWindowOpen = useChatWindowOpen();
    const launcherRef = useRef<HTMLButtonElement>(null);
    const panelId = useId();
    const [panel, setPanel] = useState<PanelView>('none');
    const [isLoading, setLoading] = useState(false);

    const closePanel = useCallback(() => {
        setPanel('none');
        launcherRef.current?.focus();
    }, []);

    useCloseOnEscape(panel !== 'none', closePanel);
    useChatWindowFocus(isWindowOpen, launcherRef);

    const start = async () => {
        const launcher = launcherRef.current;
        if (!launcher) return;

        setPanel('none');
        setLoading(true);
        launcher.focus();
        try {
            await startChatwerk(buildChatwerkConfig(organizationId), launcher);
        } catch {
            setPanel('error');
        } finally {
            setLoading(false);
        }
    };

    const handleLauncherClick = () => {
        // From the first start on, ChatWerk's own handler opens the window.
        if (isChatwerkStarted() || isLoading) return;

        if (isGranted) void start();
        else setPanel((current) => (current === 'consent' ? 'none' : 'consent'));
    };

    const handleStart = () => {
        grantService(CHATWERK_SERVICE_ID);
        void start();
    };

    // Hidden, never unmounted: ChatWerk's handler is bound to this launcher node.
    return (
        <div className="chat-widget" hidden={isBannerVisible || isWindowOpen}>
            <ChatLauncher
                ref={launcherRef}
                panelId={panelId}
                expanded={panel !== 'none'}
                busy={isLoading}
                onClick={handleLauncherClick}
            />
            {panel === 'consent' && (
                <ChatConsentPrompt id={panelId} privacyHref={privacyHref} onStart={handleStart} onClose={closePanel} />
            )}
            {panel === 'error' && <ChatLoadError id={panelId} onClose={closePanel} />}
        </div>
    );
}
